import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  Tooltip,
  Link,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  SettingsBackupRestore as RestoreIcon,
  AutoFixHigh as AutoNumberIcon,
} from '@mui/icons-material';
import { EasyCheckButton, EasyCheckTextField } from '../../common/Controls';
import { bankBackupService } from '../../../services/banks/bankBackupService';
import { styled } from '@mui/material/styles';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDragOver',
})(({ theme, isDragOver }) => ({
  border: `2px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.grey[300]}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: isDragOver ? theme.palette.primary.light + '10' : theme.palette.grey[50],
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + '10',
  },
}));

const STATUS_META = {
  NEW: { label: 'New', color: 'success' },
  EXISTS_SAME_COMPANY: { label: 'Exists', color: 'primary' },
  CONFLICT_OTHER_COMPANY: { label: 'In use elsewhere', color: 'warning' },
  CONFLICT_INACTIVE: { label: 'Conflict', color: 'error' },
  INVALID: { label: 'Invalid', color: 'error' },
};

const RESULT_META = {
  CREATED: { label: 'Created', color: 'success' },
  TEMPLATE_UPDATED: { label: 'Template updated', color: 'success' },
  SKIPPED: { label: 'Skipped', color: 'default' },
  FAILED: { label: 'Failed', color: 'error' },
};

// Row actions (mirrors BankBackupController.BankImportItem.Mode)
const MODE = {
  CREATE: 'create',
  OVERWRITE: 'overwrite-template',
  CREATE_AUTO: 'create-auto',
  APPLY_TO_BANK: 'apply-to-bank',
  CREATE_AS: 'create-as',
  SKIP: 'skip',
};

const MODE_LABELS = {
  [MODE.CREATE]: 'Create bank + template',
  [MODE.OVERWRITE]: 'Overwrite template',
  [MODE.CREATE_AUTO]: 'Create with auto-generated account number',
  [MODE.APPLY_TO_BANK]: 'Apply template to existing bank…',
  [MODE.CREATE_AS]: 'Create as new account number…',
  [MODE.SKIP]: 'Skip',
};

const CONFLICT_STATUSES = ['CONFLICT_OTHER_COMPANY', 'CONFLICT_INACTIVE'];
const ACCOUNT_NUMBER_REGEX = /^\d{8,20}$/;

const pick = (obj, pascal, camel) => (obj?.[pascal] !== undefined ? obj[pascal] : obj?.[camel]);

// Which actions a row may take, given its validation status and whether the file carries a template for it.
const optionsForRow = (status, hasTemplate) => {
  let modes;
  if (status === 'NEW') {
    modes = [MODE.CREATE, MODE.CREATE_AUTO, MODE.APPLY_TO_BANK, MODE.SKIP];
  } else if (status === 'EXISTS_SAME_COMPANY') {
    modes = [MODE.OVERWRITE, MODE.APPLY_TO_BANK, MODE.SKIP];
  } else if (CONFLICT_STATUSES.includes(status)) {
    modes = [MODE.CREATE_AUTO, MODE.APPLY_TO_BANK, MODE.CREATE_AS, MODE.SKIP];
  } else {
    modes = [MODE.SKIP];
  }
  // Nothing to apply when the file has no template for this bank
  return hasTemplate ? modes : modes.filter((m) => m !== MODE.APPLY_TO_BANK);
};

const bankLabel = (bank) =>
  `${bank.accountName || ''} · ${bank.accountNumber || ''}${bank.currency ? ` · ${bank.currency}` : ''}`;

// Restore banks + cheque templates from an exported JSON config file.
// Upload -> Validate (server dry run) -> Review & Confirm (per-row action) -> Complete.
// `banks` = the importing company's active banks (for "Apply template to existing bank").
const BankConfigRestoreDialog = ({ open, onClose, onImportComplete, banks = [] }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [configDocument, setConfigDocument] = useState(null);
  const [fileName, setFileName] = useState('');
  const [validation, setValidation] = useState([]);
  // { [accountNumber]: { mode, targetBankId, newAccountNumber } }
  const [rowActions, setRowActions] = useState({});
  const [results, setResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleClose = () => {
    if (isProcessing) return;
    setActiveStep(0);
    setConfigDocument(null);
    setFileName('');
    setValidation([]);
    setRowActions({});
    setResults(null);
    setError(null);
    onClose();
  };

  const handleFileSelect = (file) => {
    setError(null);
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.json')) {
      setError('Please select an EasyCheck bank configuration file (.json)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        const schemaVersion = pick(parsed, 'SchemaVersion', 'schemaVersion');
        const banksInDoc = pick(parsed, 'Banks', 'banks');
        if (schemaVersion !== 1 || !Array.isArray(banksInDoc) || banksInDoc.length === 0) {
          setError('This file is not a valid EasyCheck bank configuration export');
          return;
        }
        setConfigDocument(parsed);
        setFileName(file.name);
      } catch (err) {
        setError('Could not read the file — it is not valid JSON');
      }
    };
    reader.readAsText(file);
  };

  const banksInFile = () => pick(configDocument, 'Banks', 'banks') || [];

  const rowStatus = (row) => pick(row, 'Status', 'status');
  const rowAccount = (row) => pick(row, 'AccountNumber', 'accountNumber');
  const rowHasTemplate = (row) => pick(row, 'HasTemplateInFile', 'hasTemplateInFile') === true;

  const handleValidate = async () => {
    setIsProcessing(true);
    setError(null);
    setActiveStep(1);
    try {
      const rows = await bankBackupService.validateImport({
        schemaVersion: 1,
        banks: banksInFile(),
      });
      setValidation(rows);
      // Default actions: NEW -> create; everything else -> skip (never overwrite / re-number silently)
      const actions = {};
      rows.forEach((row) => {
        actions[rowAccount(row)] = { mode: rowStatus(row) === 'NEW' ? MODE.CREATE : MODE.SKIP };
      });
      setRowActions(actions);
      setActiveStep(2);
    } catch (e) {
      setError(e.message || 'Validation failed');
      setActiveStep(0);
    } finally {
      setIsProcessing(false);
    }
  };

  const updateRowAction = (accountNumber, patch) =>
    setRowActions((prev) => ({
      ...prev,
      [accountNumber]: { ...(prev[accountNumber] || { mode: MODE.SKIP }), ...patch },
    }));

  const conflictRows = validation.filter((row) => CONFLICT_STATUSES.includes(rowStatus(row)));

  // One-click path for companies that hold cheque layouts for banks they have no account with
  const handleAutoNumberAllConflicts = () => {
    setRowActions((prev) => {
      const next = { ...prev };
      conflictRows.forEach((row) => {
        next[rowAccount(row)] = { mode: MODE.CREATE_AUTO };
      });
      return next;
    });
  };

  const handleResetAllToSkip = () => {
    setRowActions((prev) => {
      const next = {};
      Object.keys(prev).forEach((acct) => {
        next[acct] = { mode: MODE.SKIP };
      });
      return next;
    });
  };

  // Target banks already claimed by another apply-to-bank row
  const targetBankIdsInUse = (exceptAccount) =>
    Object.entries(rowActions)
      .filter(([acct, a]) => acct !== exceptAccount && a.mode === MODE.APPLY_TO_BANK && a.targetBankId)
      .map(([, a]) => String(a.targetBankId));

  // Inline validation for a typed account number; null when acceptable
  const newNumberError = (accountNumber, value) => {
    const v = (value || '').trim();
    if (!ACCOUNT_NUMBER_REGEX.test(v)) return 'Enter 8–20 digits';
    const clashInFile = validation.some((row) => rowAccount(row) !== accountNumber && String(rowAccount(row)) === v);
    const clashTyped = Object.entries(rowActions).some(
      ([acct, a]) => acct !== accountNumber && a.mode === MODE.CREATE_AS && (a.newAccountNumber || '').trim() === v
    );
    if (clashInFile || clashTyped) return 'Already used by another row';
    if (banks.some((b) => String(b.accountNumber) === v)) return 'Already exists in your banks';
    return null;
  };

  const isActionComplete = (accountNumber, action) => {
    if (!action || action.mode === MODE.SKIP) return false;
    if (action.mode === MODE.APPLY_TO_BANK) return Boolean(action.targetBankId);
    if (action.mode === MODE.CREATE_AS) return newNumberError(accountNumber, action.newAccountNumber) === null;
    return true;
  };

  const nonSkipEntries = Object.entries(rowActions).filter(([, a]) => a.mode !== MODE.SKIP);
  const actionableCount = nonSkipEntries.filter(([acct, a]) => isActionComplete(acct, a)).length;
  const incompleteCount = nonSkipEntries.length - actionableCount;
  const autoCount = nonSkipEntries.filter(([, a]) => a.mode === MODE.CREATE_AUTO).length;

  const handleApply = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      const banksPayload = banksInFile().map((bank) => {
        const accountNumber = pick(bank, 'AccountNumber', 'accountNumber');
        const action = rowActions[accountNumber] || { mode: MODE.SKIP };
        const item = { ...bank, mode: action.mode };
        if (action.mode === MODE.APPLY_TO_BANK) item.targetBankId = Number(action.targetBankId);
        if (action.mode === MODE.CREATE_AS) item.newAccountNumber = (action.newAccountNumber || '').trim();
        return item;
      });
      const rows = await bankBackupService.applyImport({ schemaVersion: 1, banks: banksPayload });
      setResults(rows);
      setActiveStep(3);
      onImportComplete?.();
    } catch (e) {
      setError(e.message || 'Import failed — no changes were applied');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderSecondaryControl = (accountNumber, action) => {
    if (action.mode === MODE.CREATE_AUTO) {
      return (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          A unique placeholder number will be generated
        </Typography>
      );
    }
    if (action.mode === MODE.APPLY_TO_BANK) {
      const taken = targetBankIdsInUse(accountNumber);
      const choices = banks.filter((b) => !taken.includes(String(b.id)));
      return (
        <Select
          size="small"
          displayEmpty
          value={action.targetBankId ? String(action.targetBankId) : ''}
          onChange={(e) => updateRowAction(accountNumber, { targetBankId: e.target.value })}
          sx={{ minWidth: 240, mt: 0.5 }}
          renderValue={(selected) => {
            if (!selected) return <em>Choose bank…</em>;
            const b = banks.find((x) => String(x.id) === String(selected));
            return b ? bankLabel(b) : selected;
          }}
        >
          {choices.length === 0 && (
            <MenuItem disabled value="">
              No available banks
            </MenuItem>
          )}
          {choices.map((b) => (
            <MenuItem key={b.id} value={String(b.id)}>
              {bankLabel(b)}
            </MenuItem>
          ))}
        </Select>
      );
    }
    if (action.mode === MODE.CREATE_AS) {
      const err = newNumberError(accountNumber, action.newAccountNumber);
      const value = action.newAccountNumber || '';
      return (
        <EasyCheckTextField
          variant="compact"
          size="small"
          placeholder="Real account number"
          value={value}
          onChange={(e) =>
            updateRowAction(accountNumber, { newAccountNumber: e.target.value.replace(/\D/g, '').slice(0, 20) })
          }
          error={value !== '' && err !== null}
          helperText={value !== '' ? err || ' ' : 'Enter 8–20 digits'}
          inputProps={{ inputMode: 'numeric', maxLength: 20 }}
          sx={{ minWidth: 240, mt: 0.5 }}
        />
      );
    }
    return null;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <RestoreIcon color="primary" />
          <Typography variant="h6">Restore Bank Configuration</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} orientation="vertical">
          {/* Step 1: Upload */}
          <Step>
            <StepLabel>Upload Configuration File</StepLabel>
            <StepContent>
              <UploadArea
                isDragOver={isDragOver}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragOver(false);
                  handleFileSelect(e.dataTransfer.files[0]);
                }}
              >
                <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {fileName || 'Drop your bank configuration file here, or click to browse'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  JSON file downloaded via "Export Config" (easycheck-bank-config-*.json)
                </Typography>
                <VisuallyHiddenInput
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  onChange={(e) => {
                    handleFileSelect(e.target.files[0]);
                    e.target.value = '';
                  }}
                />
              </UploadArea>
              {configDocument && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  File contains <strong>{banksInFile().length}</strong> bank account(s),{' '}
                  <strong>{banksInFile().filter((b) => pick(b, 'Template', 'template')).length}</strong> with a cheque template.
                </Alert>
              )}
              <Box sx={{ mt: 2 }}>
                <EasyCheckButton onClick={handleValidate} disabled={!configDocument || isProcessing}>
                  Validate
                </EasyCheckButton>
              </Box>
            </StepContent>
          </Step>

          {/* Step 2: Validate */}
          <Step>
            <StepLabel>Validate</StepLabel>
            <StepContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2">Checking banks against your account...</Typography>
              </Box>
            </StepContent>
          </Step>

          {/* Step 3: Review & Confirm */}
          <Step>
            <StepLabel>Review & Confirm</StepLabel>
            <StepContent>
              {conflictRows.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>{conflictRows.length}</strong> account number(s) in this file belong to another company (for
                  example a demo account). Use <strong>Auto-generate numbers</strong> to import all of their templates
                  under unique placeholder numbers, or per row choose one of your banks to receive the template or enter
                  your real account number.
                </Alert>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                <EasyCheckButton
                  variant="outlined"
                  size="small"
                  startIcon={<AutoNumberIcon />}
                  onClick={handleAutoNumberAllConflicts}
                  disabled={conflictRows.length === 0 || isProcessing}
                >
                  Auto-generate numbers for all conflicting rows
                </EasyCheckButton>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  onClick={handleResetAllToSkip}
                  disabled={isProcessing}
                >
                  Reset all to Skip
                </Link>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validation.map((row, index) => {
                      const accountNumber = rowAccount(row);
                      const status = rowStatus(row);
                      const message = pick(row, 'Message', 'message');
                      const meta = STATUS_META[status] || { label: status, color: 'default' };
                      const locked = status === 'INVALID';
                      const action = rowActions[accountNumber] || { mode: MODE.SKIP };
                      const options = optionsForRow(status, rowHasTemplate(row));
                      return (
                        <TableRow key={`${accountNumber}-${index}`} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {pick(row, 'AccountName', 'accountName')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {accountNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{message}</Typography>
                          </TableCell>
                          <TableCell>
                            {locked ? (
                              <Tooltip title={message || ''}>
                                <Chip size="small" label="Skip" variant="outlined" />
                              </Tooltip>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Select
                                  size="small"
                                  value={options.includes(action.mode) ? action.mode : MODE.SKIP}
                                  onChange={(e) => updateRowAction(accountNumber, { mode: e.target.value })}
                                  sx={{ minWidth: 240 }}
                                >
                                  {options.map((m) => (
                                    <MenuItem key={m} value={m}>
                                      {MODE_LABELS[m]}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {renderSecondaryControl(accountNumber, action)}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {incompleteCount > 0 ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {incompleteCount} row(s) still need a target bank or a valid account number before you can import.
                </Alert>
              ) : (
                <Alert severity={actionableCount > 0 ? 'info' : 'warning'} sx={{ mt: 2 }}>
                  {actionableCount > 0
                    ? `${actionableCount} bank(s) will be processed.${
                        autoCount > 0 ? ` ${autoCount} will receive an auto-generated placeholder account number.` : ''
                      } Overwritten templates are snapshotted first and can be restored from Version History.`
                    : 'Nothing selected to import — choose an action for at least one bank.'}
                </Alert>
              )}

              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <EasyCheckButton
                  onClick={handleApply}
                  disabled={actionableCount === 0 || incompleteCount > 0 || isProcessing}
                  loading={isProcessing}
                >
                  Import
                </EasyCheckButton>
                <EasyCheckButton variant="outlined" onClick={() => setActiveStep(0)} disabled={isProcessing}>
                  Back
                </EasyCheckButton>
              </Box>
            </StepContent>
          </Step>

          {/* Step 4: Complete */}
          <Step>
            <StepLabel>Complete</StepLabel>
            <StepContent>
              {results && (
                <>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Account Number</TableCell>
                          <TableCell>Result</TableCell>
                          <TableCell>Details</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {results.map((row, index) => {
                          const action = pick(row, 'Action', 'action');
                          const meta = RESULT_META[action] || { label: action, color: 'default' };
                          return (
                            <TableRow key={`${pick(row, 'AccountNumber', 'accountNumber')}-${index}`}>
                              <TableCell>{pick(row, 'AccountNumber', 'accountNumber')}</TableCell>
                              <TableCell>
                                <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">{pick(row, 'Message', 'message')}</Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Auto-generated account numbers are placeholders. Edit the bank later to set a real account number if
                    needed.
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <EasyCheckButton onClick={handleClose}>Done</EasyCheckButton>
                  </Box>
                </>
              )}
            </StepContent>
          </Step>
        </Stepper>
      </DialogContent>
      <DialogActions>
        <EasyCheckButton variant="outlined" onClick={handleClose} disabled={isProcessing}>
          Close
        </EasyCheckButton>
      </DialogActions>
    </Dialog>
  );
};

export default BankConfigRestoreDialog;
